import React from "react";
import { Calendar, Building2, CheckCircle2, Star } from "lucide-react";

const Experience = () => {
  const timeline = [
    {
      year: "1990-2000",
      title: "Fundación y Primeros Pasos",
      description:
        "Inicio en desarrollo de software para procesos de fabricación y sistemas administrativos básicos.",
      projects: [
        "Sistemas de inventario",
        "Control de producción",
        "Nóminas automatizadas",
      ],
    },
    {
      year: "2000-2010",
      title: "Expansión y Especialización",
      description:
        "Crecimiento en el mercado con soluciones más complejas para medianas empresas.",
      projects: [
        "ERP personalizado",
        "Sistemas de calidad",
        "Integración de bases de datos",
      ],
    },
    {
      year: "2010-2020",
      title: "Transformación Digital",
      description:
        "Adopción de tecnologías web y móviles, inicio en automatización de procesos.",
      projects: [
        "Aplicaciones móviles",
        "Portales web",
        "APIs de integración",
        "Workflows automatizados",
      ],
    },
    {
      year: "2020-2024",
      title: "Innovación con IA",
      description:
        "Integración de inteligencia artificial, machine learning y herramientas de automatización avanzadas.",
      projects: [
        "IA predictiva",
        "Automatización N8N",
        "Análisis de big data",
        "Chatbots inteligentes",
      ],
    },
  ];

  const sectors = [
    {
      name: "Manufactura",
      image: "/src/assets/manufactura.png",
      projects: "120+",
    },
    { name: "Logística", image: "/src/assets/logistica.png", projects: "85+" },
    { name: "Retail", image: "/src/assets/retail.png", projects: "95+" },
    { name: "Salud", image: "/src/assets/salud.png", projects: "45+" },
    { name: "Educación", image: "/src/assets/educacion.png", projects: "35+" },
    { name: "Finanzas", image: "/src/assets/finanzas.png", projects: "60+" },
  ];

  const testimonials = [
    {
      name: "Carlos Mendoza",
      position: "Director de TI, IndustriaCorp",
      quote:
        "ModeloSmart transformó completamente nuestros procesos de producción. La automatización que implementaron nos ahorró 40% en tiempos operativos.",
    },
    {
      name: "Ana García",
      position: "CEO, LogiMax Solutions",
      quote:
        "Su experiencia de 30 años se nota en cada detalle. El sistema que desarrollaron maneja perfectamente nuestro volumen de 10,000 envíos diarios.",
    },
    {
      name: "Roberto Silva",
      position: "Gerente General, TechnoMed",
      quote:
        "La integración de IA que ModeloSmart desarrolló para nosotros ha mejorado la precisión de nuestros diagnósticos en un 35%.",
    },
  ];

  return (
    <section id="experiencia" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Nuestra <span className="text-blue-600">Experiencia</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Más de tres décadas construyendo soluciones tecnológicas que han
            transformado empresas en múltiples sectores industriales.
          </p>
        </div>

        {/* Timeline */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-12 text-center">
            Nuestra Evolución Tecnológica
          </h3>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-600 to-teal-600 hidden md:block"></div>

            <div className="space-y-12">
              {timeline.map((period, index) => (
                <div key={index} className="relative flex items-start">
                  {/* Timeline dot */}
                  <div className="hidden md:flex w-16 h-16 bg-blue-600 rounded-full items-center justify-center text-white font-bold text-sm mr-8 flex-shrink-0 shadow-lg">
                    <Calendar className="h-6 w-6" />
                  </div>

                  {/* Content */}
                  <div className="bg-gray-50 p-8 rounded-2xl flex-1 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-blue-600 font-bold text-lg">
                        {period.year}
                      </span>
                      <h4 className="text-xl font-bold text-gray-900">
                        {period.title}
                      </h4>
                    </div>
                    <p className="text-gray-600 mb-6">{period.description}</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {period.projects.map((project, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">
                            {project}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sectors */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-12 text-center">
            Sectores Atendidos
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {sectors.map((sector, index) => (
              <div
                key={index}
                className="relative bg-white p-6 rounded-2xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 text-center group h-40 flex flex-col justify-center items-center"
                style={{
                  backgroundImage: `url(${sector.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {/* Overlay para oscurecer un poco la imagen */}
                <div className="absolute inset-0 bg-black/40 rounded-2xl"></div>

                {/* Contenido encima */}
                <div className="relative z-10">
                  <h4 className="font-semibold text-yellow-400 mb-2">
                    {sector.name}
                  </h4>
                  <span className="text-yellow-500 font-bold">
                    {sector.projects}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-12 text-center">
            Lo que Dicen Nuestros Clientes
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-2xl border border-blue-100 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <div className="font-semibold text-gray-900">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-gray-600">
                    {testimonial.position}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
